/**
 * Environment configuration utility
 * Provides type-safe access to environment variables
 */

export interface AppConfig {
  // Application
  appName: string
  appVersion: string
  appDescription: string
  nodeEnv: string

  // API
  apiBaseUrl: string
  apiTimeout: number

  // WebSocket
  wsUrl: string

  // JWT Configuration
  jwtIssuer?: string
  jwtAudience?: string
  jwtRefreshThreshold: number

  // Feature flags
  enableMockApi: boolean
  enableDebug: boolean
  enablePwa: boolean
  enableAnalytics: boolean
  enableErrorReporting: boolean

  // Security
  enableCsp: boolean
  sessionTimeout: number
  tokenRefreshBuffer: number
}

/**
 * Get environment variable with type conversion
 */
function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key] || defaultValue
  if (value === undefined) {
    throw new Error(`Environment variable ${key} is required but not defined`)
  }
  return value
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, defaultValue = false): boolean {
  const value = import.meta.env[key]
  if (value === undefined) return defaultValue
  return value === 'true' || value === '1'
}

/**
 * Get number environment variable
 */
function getNumberEnvVar(key: string, defaultValue?: number): number {
  const value = import.meta.env[key]
  if (value === undefined) {
    if (defaultValue === undefined) {
      throw new Error(`Environment variable ${key} is required but not defined`)
    }
    return defaultValue
  }
  const parsed = parseInt(value, 10)
  if (isNaN(parsed)) {
    throw new Error(`Environment variable ${key} must be a valid number`)
  }
  return parsed
}

/**
 * Application configuration
 */
export const config: AppConfig = {
  // Application
  appName: getEnvVar('VITE_APP_NAME', 'Asl Marka'),
  appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  appDescription: getEnvVar('VITE_APP_DESCRIPTION', 'Digital Marking System'),
  nodeEnv: getEnvVar('VITE_NODE_ENV', 'development'),

  // API
  apiBaseUrl: getEnvVar('VITE_API_BASE', 'https://aslmarka.uz/api'),
  apiTimeout: getNumberEnvVar('VITE_API_TIMEOUT', 30000),

  // WebSocket
  wsUrl: getEnvVar('VITE_WS_URL', 'ws://localhost:8000/ws'),

  // JWT Configuration
  jwtIssuer: getEnvVar('VITE_JWT_ISSUER', undefined),
  jwtAudience: getEnvVar('VITE_JWT_AUDIENCE', undefined),
  jwtRefreshThreshold: getNumberEnvVar('VITE_JWT_REFRESH_THRESHOLD', 300000), // 5 minutes

  // Feature flags
  enableMockApi: getBooleanEnvVar('VITE_ENABLE_MOCK_API', false),
  enableDebug: getBooleanEnvVar('VITE_ENABLE_DEBUG', false),
  enablePwa: getBooleanEnvVar('VITE_ENABLE_PWA', false),
  enableAnalytics: getBooleanEnvVar('VITE_ENABLE_ANALYTICS', false),
  enableErrorReporting: getBooleanEnvVar('VITE_ENABLE_ERROR_REPORTING', false),

  // Security
  enableCsp: getBooleanEnvVar('VITE_ENABLE_CSP', true),
  sessionTimeout: getNumberEnvVar('VITE_SESSION_TIMEOUT', 3600000),
  tokenRefreshBuffer: getNumberEnvVar('VITE_TOKEN_REFRESH_BUFFER', 300000), // 5 minutes
}

/**
 * Check if running in development mode
 */
export const isDevelopment = config.nodeEnv === 'development'

/**
 * Check if running in production mode
 */
export const isProduction = config.nodeEnv === 'production'

/**
 * Check if running in staging mode
 */
export const isStaging = config.nodeEnv === 'staging'

/**
 * Get build information
 */
export const buildInfo = {
  version: config.appVersion,
  nodeEnv: config.nodeEnv,
}

// Log configuration in development
if (isDevelopment && config.enableDebug) {
  console.group('🔧 Application Configuration')
  console.table(config)
  console.groupEnd()
}
